import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { FollowupStep, FollowupPreviewDTO } from "@ai-crm/shared";
import type { FollowupEditorServices } from "./followup-settings";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select } from "./ui/select";
import { Textarea } from "./ui/textarea";
type ContentDraft = {
  mode: FollowupStep["mode"];
  text: string;
  instruction: string;
  templateRef: string;
  values: string[];
};
export function FollowupStepContentEditor({
  index,
  draft,
  step,
  update,
  versions,
  channels = [],
  loadTemplates,
  preview,
}: FollowupEditorServices & {
  index: number;
  draft: ContentDraft;
  step: FollowupStep;
  update: (patch: Partial<ContentDraft>) => void;
  versions: { expectedVersion: number; expectedCapabilitiesVersion: number };
}) {
  const waba = channels.filter(
    (c) => c.transport === "WABA" && c.physicalChannelId,
  );
  const [channelId, setChannelId] = useState(waba[0]?.physicalChannelId ?? "");
  const query = useQuery({
    queryKey: ["followup-templates", channelId],
    queryFn: () => loadTemplates!(channelId),
    enabled:
      draft.mode === "WABA_TEMPLATE" && Boolean(channelId && loadTemplates),
    retry: false,
  });
  const [result, setResult] = useState<FollowupPreviewDTO | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const request = useRef<AbortController | null>(null);
  const signature = JSON.stringify([step, versions]);
  useEffect(() => {
    request.current?.abort();
    request.current = null;
    setResult(null);
    setPending(false);
    setError(null);
    return () => {
      request.current?.abort();
      request.current = null;
    };
  }, [signature, preview]);
  async function runPreview() {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setPending(true);
    setError(null);
    setResult(null);
    try {
      const response = await preview!({ ...versions, step }, controller.signal);
      if (request.current === controller && !controller.signal.aborted)
        setResult(response);
    } catch (e) {
      if (request.current === controller && !controller.signal.aborted)
        setError(
          e instanceof Error
            ? e.message
            : "Не удалось подготовить предпросмотр",
        );
    } finally {
      if (request.current === controller) setPending(false);
    }
  }
  const selected = query.data?.items.find(
    (item) => item.ref === draft.templateRef,
  );
  return (
    <div className="space-y-2">
      <label className="block space-y-2 text-sm font-medium">
        Режим шага {index + 1}
        <Select
          aria-label={`Режим шага ${index + 1}`}
          value={draft.mode}
          onChange={(e) =>
            update({ mode: e.target.value as FollowupStep["mode"] })
          }
        >
          <option value="FIXED">Текст</option>
          <option value="PROMPT">По промпту</option>
          <option value="WABA_TEMPLATE">Шаблон WABA</option>
        </Select>
      </label>
      {draft.mode === "FIXED" && (
        <Textarea
          aria-label={`Текст шага ${index + 1}`}
          maxLength={5000}
          value={draft.text}
          onChange={(e) => update({ text: e.target.value })}
        />
      )}
      {draft.mode === "PROMPT" && (
        <>
          <Textarea
            aria-label={`Инструкция шага ${index + 1}`}
            maxLength={10000}
            value={draft.instruction}
            onChange={(e) => update({ instruction: e.target.value })}
          />
          <p>
            Опишите цель одного касания. Переписка будет добавлена при отправке.
          </p>
        </>
      )}
      {draft.mode === "WABA_TEMPLATE" && (
        <div>
          {!waba.length && (
            <p role="status">
              Нет подключённых каналов WhatsApp Business. Этот режим применяется
              только к WABA.
            </p>
          )}
          <Select
            className="mb-2"
            aria-label={`Канал шаблонов шага ${index + 1}`}
            value={channelId}
            onChange={(e) => setChannelId(e.target.value)}
          >
            <option value="">Выберите канал</option>
            {waba.map((c) => (
              <option key={c.physicalChannelId} value={c.physicalChannelId}>
                {c.wazzupChannelId}
              </option>
            ))}
          </Select>
          {channelId && query.isPending && (
            <p role="status">Загрузка шаблонов…</p>
          )}
          {query.error && (
            <p role="alert">
              {"status" in query.error && query.error.status === 403
                ? "Нет доступа к каталогу"
                : "status" in query.error && query.error.status === 503
                  ? "Сервис шаблонов временно недоступен"
                  : "Каталог недоступен"}
              : {query.error.message}
            </p>
          )}
          {query.data && !query.data.items.length && (
            <p role="status">В этом канале шаблонов пока нет.</p>
          )}
          {query.data && draft.templateRef && !selected && (
            <p role="alert">
              Сохранённый шаблон удалён или недоступен в выбранном канале.
              Выберите шаблон заново.
            </p>
          )}
          <Select
            className="mb-2"
            aria-label={`Шаблон шага ${index + 1}`}
            value={draft.templateRef}
            onChange={(e) => {
              const item = query.data?.items.find(
                (v) => v.ref === e.target.value,
              );
              update({
                templateRef: e.target.value,
                values: item?.variables.map(() => "") ?? [],
              });
            }}
          >
            <option value="">Выберите одобренный шаблон</option>
            {draft.templateRef && !selected && (
              <option value={draft.templateRef} disabled>
                Сохранённый шаблон недоступен
              </option>
            )}
            {query.data?.items.map((t) => (
              <option
                key={t.ref}
                value={t.ref}
                disabled={!t.supported || t.status !== "APPROVED"}
              >
                {t.name} · {t.language} · {t.status}
                {!t.supported ? " — этот формат пока недоступен" : ""}
              </option>
            ))}
          </Select>
          {selected && (
            <>
              <p className="whitespace-pre-wrap">{selected.preview}</p>
              <p>
                Параметров: {selected.variables.length}. Каналы:{" "}
                {selected.channelIds.join(", ")}
              </p>
              {!selected.supported && (
                <p role="alert">Этот формат пока недоступен</p>
              )}
              {selected.variables.map((v, n) => (
                <label key={v.index} className="block">
                  {v.label}
                  <Input
                    className="mt-2"
                    aria-label={`Параметр ${n + 1} шага ${index + 1}`}
                    maxLength={1000}
                    value={draft.values[n] ?? ""}
                    onChange={(e) =>
                      update({
                        values: selected.variables.map((_, i) =>
                          i === n ? e.target.value : (draft.values[i] ?? ""),
                        ),
                      })
                    }
                  />
                </label>
              ))}
            </>
          )}
          {channelId && (
            <Button variant="outline" size="sm" type="button" onClick={() => void query.refetch()}>
              Обновить каталог
            </Button>
          )}
        </div>
      )}
      {preview && (
        <Button
          variant="secondary"
          type="button"
          disabled={pending}
          onClick={() => void runPreview()}
        >
          {pending
            ? "Подготовка предпросмотра…"
            : `Предпросмотр шага ${index + 1}`}
        </Button>
      )}
      {error && <p role="alert">{error}</p>}
      {result && (
        <div>
          <p className="whitespace-pre-wrap">{result.text}</p>
          {result.warnings.map((w) => (
            <p key={w}>{w}</p>
          ))}
          <p>Это предпросмотр. Сообщение не отправлено.</p>
        </div>
      )}
    </div>
  );
}
